import { useCallback, useEffect, useState } from "react";
import * as Immutable from "immutable";
import {
  ApiEntity,
  AtlasValues,
  BaseRequest,
  Boundaries,
  CoreApi,
  IdKeyType,
  LabelsOptions,
  ProjectionsList,
  RequestOptions,
  SearchOptions,
  Size,
  StylesOptions,
} from "@hokuto/jam-core";
import {
  JamAtlasParams,
  JamMapParams,
  JamParamsChangeProps,
} from "../types.js";
import { isAtlasParams, isMapParams } from "../guardians.js";
import { useBase } from "./base.js";
import { ParamsObserver } from "../utils/index.js";

export function useCanvasParams(
  args: ReturnType<typeof useBase> &
    BaseRequest & {
      [IdKeyType.Map]?: string;
      [IdKeyType.Atlas]?: string;
      [ApiEntity.Atlas]?: AtlasValues;
      projection?: ProjectionsList;
      paramsObserver?: ParamsObserver;
    }
) {
  const mapId = isMapParams(args) ? args[IdKeyType.Map] : undefined;
  const atlasId = isAtlasParams(args) ? args[IdKeyType.Atlas] : undefined;
  const atlas = isAtlasParams(args) ? args[ApiEntity.Atlas] : undefined;

  if (!atlas && !mapId && !atlasId)
    throw new Error(`Expected atlas, atlasId or mapId`);

  const { scale, width, height, paramsObserver, bgColor, ...props } = args;

  const [mapParams, setMapParams] = useState<JamMapParams>();
  const [atlasParams, setAtlasParams] = useState<JamAtlasParams>();

  const baseParams = {
    ...props,
    width,
    height,
    scale,
  };

  const params = { ...props, ...baseParams };

  const paramsId = Immutable.fromJS({
    ...CoreApi.Options.getRequestOptions(params),
    mapId,
    atlasId,
    width,
    height,
  }).hashCode();

  const handleChange = useCallback(() => {
    if (isMapParams(args)) {
      const { [IdKeyType.Map]: mapId } = args;
      const mapParams = { ...params, [IdKeyType.Map]: mapId };
      paramsObserver?.setParams({ mapParams });
      return setMapParams(mapParams);
    } else if (isAtlasParams(args)) {
      const { [ApiEntity.Atlas]: atlas, [IdKeyType.Atlas]: atlasId } = args;
      const atlasParams = {
        ...params,
        [IdKeyType.Atlas]: atlasId,
        [ApiEntity.Atlas]: atlas,
      };
      paramsObserver?.setParams({ atlasParams });
      return setAtlasParams(atlasParams);
    }
  }, [params, paramsObserver]);

  useEffect(() => {
    handleChange();
  }, [paramsId]);

  return {
    mapParams,
    atlasParams,
  };
}

export function useDrawerParams(
  args: JamParamsChangeProps &
    Partial<Size> &
    StylesOptions & {
      margin?: Boundaries;
      body?: Partial<RequestOptions>;
      search?: SearchOptions;
      labels?: LabelsOptions;
    }
) {
  const {
    mapParams: mapParamsOrigin,
    atlasParams: atlasParamsOrigin,
    ...params
  } = args;
  const [mapParams, setMapParams] = useState<JamMapParams>();
  const [atlasParams, setAtlasParams] = useState<JamAtlasParams>();

  const paramsId = Immutable.fromJS({
    ...params,
    mapParamsOrigin,
    atlasParamsOrigin,
  }).hashCode();

  const handleChange = useCallback(() => {
    if (mapParamsOrigin) {
      return setMapParams({
        ...mapParamsOrigin,
        ...params,
      });
    } else if (atlasParamsOrigin) {
      return setAtlasParams({
        ...atlasParamsOrigin,
        ...params,
      });
    }
  }, [params, mapParamsOrigin, atlasParamsOrigin]);

  useEffect(() => {
    handleChange();
  }, [paramsId]);

  return {
    mapParams,
    atlasParams,
  };
}
